import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, Save, Loader2, ExternalLink } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface Ad {
  id: string;
  title: string;
  body: string | null;
  image_url: string | null;
  cta_label: string | null;
  cta_url: string | null;
  active: boolean;
  display_order: number;
  created_at: string;
}

const emptyForm = {
  title: '',
  body: '',
  image_url: '',
  cta_label: '',
  cta_url: '',
  active: true,
  display_order: 0,
};

export function AdsManager() {
  const { user } = useAuth();
  const [items, setItems] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function fetchAll() {
    setLoading(true);
    const { data } = await supabase
      .from('ads')
      .select('*')
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false });
    setItems(data ?? []);
    setLoading(false);
  }

  useEffect(() => { fetchAll(); }, []);

  function openCreate() {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  }

  function openEdit(a: Ad) {
    setForm({
      title: a.title,
      body: a.body ?? '',
      image_url: a.image_url ?? '',
      cta_label: a.cta_label ?? '',
      cta_url: a.cta_url ?? '',
      active: a.active,
      display_order: a.display_order,
    });
    setEditingId(a.id);
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.title.trim()) return;
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      body: form.body.trim() || null,
      image_url: form.image_url.trim() || null,
      cta_label: form.cta_label.trim() || null,
      cta_url: form.cta_url.trim() || null,
      active: form.active,
      display_order: Number(form.display_order),
      created_by: user?.id ?? null,
    };

    if (editingId) {
      await supabase.from('ads').update(payload).eq('id', editingId);
      setMessage('Ad updated.');
    } else {
      await supabase.from('ads').insert(payload);
      setMessage('Ad created.');
    }

    setSaving(false);
    setShowModal(false);
    fetchAll();
    setTimeout(() => setMessage(null), 3000);
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this ad?')) return;
    setDeletingId(id);
    await supabase.from('ads').delete().eq('id', id);
    setDeletingId(null);
    fetchAll();
  }

  async function toggleActive(a: Ad) {
    await supabase.from('ads').update({ active: !a.active }).eq('id', a.id);
    setItems(prev => prev.map(i => i.id === a.id ? { ...i, active: !i.active } : i));
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-midnight dark:text-soft-white">Ads</h3>
          <p className="text-xs text-slate dark:text-soft-white/60 mt-0.5">
            Promotional banners shown on the Dashboard.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-electric text-white text-sm rounded-lg hover:bg-electric/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New
        </button>
      </div>

      {message && (
        <div className="mb-3 px-3 py-2 bg-emerald/10 text-emerald text-sm rounded-lg">
          {message}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-slate" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate dark:text-soft-white/50 py-6 text-center">
          No ads yet.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <div
              key={a.id}
              className="flex items-start justify-between gap-3 p-3 rounded-lg border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal"
            >
              <div className="min-w-0 flex-1 flex gap-3">
                {a.image_url && (
                  <img
                    src={a.image_url}
                    alt={a.title}
                    className="h-12 w-16 object-cover rounded shrink-0"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${a.active ? 'bg-emerald/10 text-emerald' : 'bg-sand/30 text-slate'}`}>
                      {a.active ? 'Live' : 'Hidden'}
                    </span>
                    <span className="text-[10px] text-slate/60 dark:text-soft-white/40">Order: {a.display_order}</span>
                  </div>
                  <p className="text-sm font-medium text-midnight dark:text-soft-white truncate">{a.title}</p>
                  {a.cta_url && (
                    <a
                      href={a.cta_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-electric flex items-center gap-0.5 mt-0.5 hover:underline"
                    >
                      {a.cta_label || a.cta_url}
                      <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => toggleActive(a)}
                  className="p-1.5 rounded hover:bg-sand/20 dark:hover:bg-slate/20 text-slate dark:text-soft-white/60 text-xs"
                  title={a.active ? 'Hide' : 'Show'}
                >
                  {a.active ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => openEdit(a)}
                  className="p-1.5 rounded hover:bg-sand/20 dark:hover:bg-slate/20 text-slate dark:text-soft-white/60"
                  title="Edit"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(a.id)}
                  disabled={deletingId === a.id}
                  className="p-1.5 rounded hover:bg-crimson/10 text-crimson/60 hover:text-crimson transition-colors"
                  title="Delete"
                >
                  {deletingId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-charcoal rounded-xl shadow-2xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-slate hover:text-midnight dark:hover:text-soft-white"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-base font-semibold text-midnight dark:text-soft-white mb-4">
              {editingId ? 'Edit Ad' : 'New Ad'}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate dark:text-soft-white/70 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-sand/40 dark:border-slate/40 rounded-lg px-3 py-2 text-sm text-midnight dark:text-soft-white bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-electric/30"
                  placeholder="Ad headline"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate dark:text-soft-white/70 mb-1">Body text (optional)</label>
                <textarea
                  value={form.body}
                  onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                  rows={2}
                  className="w-full border border-sand/40 dark:border-slate/40 rounded-lg px-3 py-2 text-sm text-midnight dark:text-soft-white bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-electric/30 resize-none"
                  placeholder="Short description"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate dark:text-soft-white/70 mb-1">Image URL (optional)</label>
                <input
                  type="url"
                  value={form.image_url}
                  onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                  className="w-full border border-sand/40 dark:border-slate/40 rounded-lg px-3 py-2 text-sm text-midnight dark:text-soft-white bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-electric/30"
                  placeholder="https://..."
                />
                {form.image_url && (
                  <img
                    src={form.image_url}
                    alt="preview"
                    className="mt-2 h-20 w-full object-cover rounded border border-sand/20"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate dark:text-soft-white/70 mb-1">Button label</label>
                  <input
                    type="text"
                    value={form.cta_label}
                    onChange={e => setForm(f => ({ ...f, cta_label: e.target.value }))}
                    className="w-full border border-sand/40 dark:border-slate/40 rounded-lg px-3 py-2 text-sm text-midnight dark:text-soft-white bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-electric/30"
                    placeholder="Learn More"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate dark:text-soft-white/70 mb-1">Display order</label>
                  <input
                    type="number"
                    value={form.display_order}
                    onChange={e => setForm(f => ({ ...f, display_order: Number(e.target.value) }))}
                    min={0}
                    className="w-full border border-sand/40 dark:border-slate/40 rounded-lg px-3 py-2 text-sm text-midnight dark:text-soft-white bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-electric/30"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate dark:text-soft-white/70 mb-1">Button URL</label>
                <input
                  type="url"
                  value={form.cta_url}
                  onChange={e => setForm(f => ({ ...f, cta_url: e.target.value }))}
                  className="w-full border border-sand/40 dark:border-slate/40 rounded-lg px-3 py-2 text-sm text-midnight dark:text-soft-white bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-electric/30"
                  placeholder="https://..."
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                  className="rounded border-sand/40 text-electric focus:ring-electric/30"
                />
                <span className="text-xs text-slate dark:text-soft-white/70">Active (visible to users)</span>
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-slate dark:text-soft-white/70 hover:text-midnight dark:hover:text-soft-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.title.trim()}
                className="flex items-center gap-1.5 px-4 py-2 bg-electric text-white text-sm rounded-lg hover:bg-electric/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {editingId ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
